import { checkLeaveOverlap, submitLeaveRequest } from "firebaseService/firebaseService";
import { X } from "lucide-react";
import { useState } from "react";
import { LEAVE_TYPES } from "utils/types";

export const AddLeaveForEmployeeModal = ({ employee, manager, onClose }) => {
    const [leaveType, setLeaveType] = useState('annual');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [comments, setComments] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!startDate || !endDate) { setError('Please select both a start and end date.'); return; }
        if (new Date(startDate) > new Date(endDate)) { setError('End date cannot be before the start date.'); return; }
        const result = await checkLeaveOverlap(employee.id, startDate, endDate);
        if (result.overlap) {
            setError(result.message);
            return;
        }
        setError('');
        setSubmitting(true);
        try {
            await submitLeaveRequest({
                employeeEmail: employee.email,
                employeeId: employee.id,
                employeeName: employee.name,
                managerId: manager.id,
                leaveType,
                startDate,
                endDate,
                comments: comments?.trim() || '',
                status: 'approved',
            })
            onClose();
        } catch (err) {
            console.error("Error adding leave for employee:", err);
            setError('Failed to add leave. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl p-8 w-full max-w-md">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800">Add Leave</h2>
                        <p className="text-gray-600">For: <span className="font-semibold">{employee.name}</span></p>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-800"><X size={24} /></button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label htmlFor="leaveType" className="block text-sm font-medium text-gray-700 mb-1">Leave Type</label>
                        <select id="leaveType" value={leaveType} onChange={(e) => setLeaveType(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md shadow-sm">
                            {Object.entries(LEAVE_TYPES).map(([key, { name }]) => (<option key={key} value={key}>{name}</option>))}
                        </select>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                            <input type="date" id="startDate" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md shadow-sm" />
                        </div>
                        <div>
                            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                            <input type="date" id="endDate" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md shadow-sm" />
                        </div>
                    </div>
                    <div className="mb-4">
                        <label htmlFor="comments" className="block text-sm font-medium text-gray-700 mb-1">Comments (Optional)</label>
                        <textarea
                            id="comments"
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            placeholder="Add any notes about this leave..."
                            className="w-full p-2 border border-gray-300 rounded-md shadow-sm resize-none"
                            rows={3}
                        />
                    </div>
                    <div className="mb-4">
                        <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                        <input className="w-full p-2 border rounded-md shadow-sm" value='Approved' disabled />
                    </div>
                    {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
                    <div className="flex justify-end gap-4">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancel</button>
                        <button type="submit" disabled={submitting} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300">{submitting ? 'Adding...' : 'Add Leave'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};